/**
 * Generic shimmer skeleton for detail / edit / review pages that live inside
 * the app shell. Renders breadcrumbs → header (title + actions) → 2–3 stacked
 * content cards. Pages with table-heavy layouts should use `<TableSkeleton>`
 * instead.
 */
interface PageContentSkeletonProps {
  /** When true, shows a small breadcrumb line above the heading. */
  showBreadcrumbs?: boolean;
  /** Number of action-button placeholders next to the heading. */
  actionCount?: number;
  /** Number of content cards stacked below the header. */
  cardCount?: number;
  /** Renders a narrower side-rail card alongside the first content card. */
  withSideCard?: boolean;
}

export function PageContentSkeleton({
  showBreadcrumbs = true,
  actionCount = 2,
  cardCount = 2,
  withSideCard = false,
}: PageContentSkeletonProps) {
  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6">
      {showBreadcrumbs && <SkelLine className="h-3 w-72" />}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <SkelLine className="h-7 w-72" />
        <div className="flex items-center gap-3">
          {Array.from({ length: actionCount }).map((_, i) => (
            <SkelPill key={i} className="h-9 w-32" />
          ))}
        </div>
      </div>

      <div
        className={
          withSideCard
            ? "grid grid-cols-1 gap-4 lg:grid-cols-[2fr,1fr]"
            : "flex flex-col gap-4"
        }
      >
        <div className="flex flex-col gap-4">
          {Array.from({ length: cardCount }).map((_, i) => (
            <ContentCardSkel key={i} rows={i === 0 ? 6 : 4} />
          ))}
        </div>
        {withSideCard && <SideCardSkel />}
      </div>
    </div>
  );
}

function ContentCardSkel({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-5">
      <SkelLine className="h-5 w-56" />
      <SkelLine className="h-px w-full bg-borderDefault" />
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center justify-between gap-6">
          <SkelLine
            className={`h-3 ${
              i % 3 === 0 ? "w-32" : i % 3 === 1 ? "w-44" : "w-28"
            }`}
          />
          <SkelLine
            className={`h-3 ${
              i % 4 === 0
                ? "w-20"
                : i % 4 === 1
                  ? "w-32"
                  : i % 4 === 2
                    ? "w-16"
                    : "w-24"
            }`}
          />
        </div>
      ))}
    </div>
  );
}

function SideCardSkel() {
  return (
    <div className="flex h-fit flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-5">
      <SkelLine className="h-4 w-32" />
      <SkelLine className="h-3 w-full" />
      <SkelLine className="h-3 w-11/12" />
      <SkelLine className="h-3 w-3/4" />
      <SkelPill className="h-9 w-full mt-2" />
    </div>
  );
}

function SkelLine({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-bgLight ${className}`} />;
}

function SkelPill({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-full bg-bgLight ${className}`} />;
}

export default PageContentSkeleton;
